import { computed, ref, unref } from "vue";

import { useAdminDraftQueue } from "@/composables/useAdminDraftQueue";
import { ADMIN_DRAFT_QUEUE_DESTINATION_PREVIEW } from "@/utils/adminDraftQueue.mjs";
import {
  completeAdminDraftPreviewApproval,
  runAdminDraftPreviewApproveAction,
} from "@/utils/adminVisualEditorApprove.mjs";
import {
  recordAdminDraftApprovalDiagnostic,
  startAdminDraftApprovalDiagnostic,
} from "@/utils/adminDraftApprovalDiagnostics.mjs";

const readValue = (value) => {
  if (typeof value === "function") return value();
  return unref(value);
};

const closeCurrentPreview = async () => {
  if (typeof window === "undefined") return false;

  window.close();
  if (window.closed) return true;

  await new Promise((resolve) => {
    window.setTimeout(resolve, 300);
  });
  return window.closed;
};

const callIfFunction = async (callback, value) => {
  if (typeof callback === "function") {
    await callback(value);
  }
};

export function useAdminDraftPreviewApproval({
  kind,
  bankId,
  currentId,
  route,
  router,
  canApprove,
  isComplete,
  isApproving = false,
  approve,
  fallbackAdminPath = "",
  queueActive = true,
  onSuccess,
  onApprovalError,
  onCompletionError,
  closePreview = closeCurrentPreview,
  navigateToExit = (path) => router.push(path),
  navigateDocument,
} = {}) {
  const approvePending = ref(false);
  const approvalCompleted = ref(false);
  const completionRecovery = ref(null);

  const queue = useAdminDraftQueue({
    kind,
    bankId,
    currentId,
    route,
    router,
    destination: ADMIN_DRAFT_QUEUE_DESTINATION_PREVIEW,
    enabled: queueActive,
    navigateDocument,
  });

  const approvalReady = computed(
    () =>
      Boolean(readValue(canApprove)) &&
      Boolean(readValue(isComplete)) &&
      !Boolean(readValue(isApproving)) &&
      !approvePending.value &&
      !approvalCompleted.value,
  );
  const canApproveClose = approvalReady;
  const canApproveNext = computed(
    () => approvalReady.value && queue.canAdvanceAfterRemoval.value,
  );
  const getSafeExitPath = () =>
    queue.getExitPath(String(readValue(fallbackAdminPath) || ""));

  const handleApproval = async (action = "next") => {
    completionRecovery.value = null;
    const attemptId = startAdminDraftApprovalDiagnostic({
      surface: "draft_preview",
      action,
      kind: String(readValue(kind) || ""),
      bankId: String(readValue(bankId) || ""),
      currentDraftId: String(readValue(currentId) || ""),
      queueKey: String(route?.query?.draftQueue || ""),
      queueIds: queue.rows.value.map((row) => row.id),
      currentIndex: queue.currentIndex.value,
      canApproveNext: queue.canAdvanceAfterRemoval.value,
      href: typeof window === "undefined" ? "" : window.location.href,
    });

    const result = await runAdminDraftPreviewApproveAction({
      action,
      approveInFlight: approvePending,
      approvalCompleted,
      isApproving,
      canApprove,
      isComplete,
      canApproveNext: queue.canAdvanceAfterRemoval,
      approve: async () => {
        recordAdminDraftApprovalDiagnostic(
          "approval_callable_started",
          {},
          { attemptId },
        );
        const approvalResult = await approve();
        recordAdminDraftApprovalDiagnostic(
          "approval_callable_completed",
          { result: Boolean(approvalResult) },
          { attemptId },
        );
        return approvalResult;
      },
      completeApprovedDraftAction: async (approvedAction) => {
        recordAdminDraftApprovalDiagnostic(
          "preview_completion_requested",
          {
            currentDraftId: String(readValue(currentId) || ""),
            queueIds: queue.rows.value.map((row) => row.id),
            currentIndex: queue.currentIndex.value,
            selectedNextId:
              queue.rows.value[queue.currentIndex.value + 1]?.id || "",
          },
          { attemptId },
        );
        return completeAdminDraftPreviewApproval({
          action: approvedAction,
          queue,
          fallbackPath: String(readValue(fallbackAdminPath) || ""),
          closePreview,
          navigateToExit,
        });
      },
      getExitPath: getSafeExitPath,
      onSuccess,
      onApprovalError,
      onCompletionError: async (recovery) => {
        completionRecovery.value = recovery;
        recordAdminDraftApprovalDiagnostic(
          "preview_completion_failed",
          { exitPath: recovery?.exitPath || "" },
          { attemptId, error: recovery?.error },
        );
        await callIfFunction(onCompletionError, recovery);
      },
    });

    recordAdminDraftApprovalDiagnostic(
      "approval_flow_completed",
      {
        approved: result?.approved === true,
        result: result?.status === "approved",
      },
      { attemptId, error: result?.error },
    );

    return result;
  };

  const advancePastStaleCurrent = async () => {
    if (typeof route?.query?.draftQueue !== "string") return false;

    await queue.ensureRefreshed();
    await queue.removeCurrentAndAdvance({
      fallbackPath: queue.getExitPath(
        String(readValue(fallbackAdminPath) || ""),
      ),
    });
    return true;
  };

  const recoverFromCompletionError = async () => {
    const exitPath = completionRecovery.value?.exitPath || getSafeExitPath();
    if (!exitPath) return false;

    const navigated = await navigateToExit(exitPath);
    return navigated !== false;
  };

  return {
    queue,
    approvePending,
    isApprovalPending: approvePending,
    approvalCompleted,
    canApproveClose,
    canApproveNext,
    completionRecovery,
    getSafeExitPath,
    handleApproval,
    advancePastStaleCurrent,
    recoverFromCompletionError,
  };
}
